import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
	CreateScheduledStream,
	DeleteScheduledStream,
	ScheduledStreamStatus,
	UpdateScheduledStream,
} from '../../../connection/scheduled-streams';
import { setScheduledStream } from '../../../stores/actions/meeting';
import { GreenroomState as GRS } from '../../../stores/types/types';
import Icon, { COLORS, ICONS } from '../../general-components/icon';

// TODO: will probably want to extract this to a utility function to deduplicate
const formatTime = (date: number, includeMonthAndDay: boolean = false) => {
	const dateString = new Date(date).toLocaleTimeString('en-US', {
		...(includeMonthAndDay && { month: 'short', day: 'numeric' }),
		hour: 'numeric',
		minute: 'numeric',
		timeZoneName: 'short',
	});
	const splitDateString = dateString.split(' ');
	const timeZoneString = splitDateString.pop();
	const dateWithoutTimeZoneString = splitDateString.join(' ');
	return `${dateWithoutTimeZoneString} (${timeZoneString})`;
};

// Borrowed from: https://gist.github.com/WebReflection/6076a40777b65c397b2b9b97247520f0
function toDatetimeLocal(date: Date) {
	const ten = (i: number) => (i < 10 ? '0' : '') + i;

	const YYYY = date.getFullYear();
	const MM = ten(date.getMonth() + 1);
	const DD = ten(date.getDate());
	const HH = ten(date.getHours());
	const II = ten(date.getMinutes());
	const SS = ten(date.getSeconds());

	return `${YYYY}-${MM}-${DD}T${HH}:${II}:${SS}`;
}

const getDateString = (inputDate: Date | undefined): string => (
	toDatetimeLocal(new Date(inputDate ?? Date.now()))
);

export default function DebugScheduledStream() {
	const dispatch = useDispatch();
	const token = useSelector((s: GRS) => s.SessionReducer.token);
	const user = useSelector((s: GRS) => s.SessionReducer.user);
	const meeting = useSelector((s: GRS) => s.SessionReducer.meeting);
	const scheduledStream = useSelector((s: GRS) => s.MeetingReducer.scheduledStream);

	const [isCreating, setIsCreating] = useState<boolean>(false);
	const [isEditing, setIsEditing] = useState<boolean>(false);
	const [newStartTime, setNewStartTime] = useState<string>(
		getDateString(scheduledStream?.startTime),
	);
	const [isConfirmingDelete, setIsConfirmingDelete] = useState<boolean>(false);

	async function editScheduledStream() {
		setIsEditing((cur) => !cur);
	}

	async function createScheduledStream() {
		if (token && meeting?.meeting && user?.gr_user) {
			const createdScheduledStream = await CreateScheduledStream(
				token,
				meeting.meeting,
				user.gr_user,
				new Date(newStartTime),
			);

			if (createdScheduledStream) {
				dispatch(setScheduledStream(createdScheduledStream));
				setIsCreating(false);
			}
		}
	}

	async function updateScheduledStream() {
		if (token && meeting?.meeting && scheduledStream?.id && user?.gr_user) {
			const updatedScheduledStream = await UpdateScheduledStream(
				token,
				meeting.meeting,
				scheduledStream.id,
				user.gr_user,
				new Date(newStartTime),
				ScheduledStreamStatus.Scheduled,
			);

			if (updatedScheduledStream) {
				dispatch(setScheduledStream(updatedScheduledStream));
				setIsEditing(false);
			}
		}
	}

	async function deleteScheduledStream() {
		if (token && meeting?.meeting && scheduledStream?.id && user?.gr_user) {
			const deletedScheduledStream = await DeleteScheduledStream(
				token,
				meeting.meeting,
				scheduledStream.id,
				user.gr_user,
			);

			if (deletedScheduledStream) {
				dispatch(setScheduledStream(null));
			}
		}
	}

	return (
		<div className="section scheduled-stream">
			<h5>Scheduled Stream</h5>
			{scheduledStream ? (
				<>
					<div className="scheduled-stream__row">
						<div className="scheduled-stream__row__label">Start Time</div>
						<div className="scheduled-stream__row__value">
							{isEditing ? (
								<input
									type="datetime-local"
									value={newStartTime}
									onChange={(e) => {
										setNewStartTime(e.target.value);
									}}
								/>
							) : (
								formatTime(new Date(scheduledStream.startTime).getTime(), true)
							)}
						</div>
						<div className="scheduled-stream__row__controls">
							{isEditing ? (
								<>
									<button onClick={updateScheduledStream}>
										<Icon name={ICONS.CHECK} color={COLORS.GREEN} size={16} />
									</button>
									<button onClick={editScheduledStream}>
										<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} />
									</button>
								</>
							) : (
								<button
									onClick={() => {
										setIsConfirmingDelete(false);
										editScheduledStream();
									}}
								>
									<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={16} />
								</button>
							)}
							<button
								onClick={() => {
									setIsEditing(false);
									setIsConfirmingDelete(true);
								}}
							>
								<Icon name={ICONS.TRASH} color={COLORS.WHITE} size={16} />
							</button>
						</div>
					</div>
					{isConfirmingDelete && (
						<div className="scheduled-stream__row">
							<div className="scheduled-stream__row__label">Are you sure?</div>
							<div className="scheduled-stream__row__controls">
								<button onClick={deleteScheduledStream}>
									<Icon name={ICONS.TRASH} color={COLORS.RED} size={16} />
								</button>
								<button onClick={() => setIsConfirmingDelete(false)}>
									<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} />
								</button>
							</div>
						</div>
					)}
					<div className="scheduled-stream__row status-row">
						<div className="scheduled-stream__row__label">Status</div>
						<div className="scheduled-stream__row__value status-value">
							{scheduledStream.status ?? 'unknown'}
						</div>
					</div>
				</>
			) : (
				<>
					<div className="scheduled-stream__row">
						<div className="scheduled-stream__row__label">
							No scheduled stream
						</div>
						<div className="scheduled-stream__row__value">Create new?</div>
						<div className="scheduled-stream__row__controls">
							<button
								onClick={() => {
									setIsCreating(true);
									setIsEditing(false);
									setIsConfirmingDelete(false);
								}}
							>
								<Icon name={ICONS.ADD} color={COLORS.WHITE} size={16} />
							</button>
						</div>
					</div>
					{isCreating && (
						<div className="scheduled-stream__row">
							<div className="scheduled-stream__row__label">Start Time</div>
							<div className="scheduled-stream__row__value">
								<input
									type="datetime-local"
									value={newStartTime}
									onChange={(e) => {
										setNewStartTime(e.target.value);
									}}
								/>
							</div>
							<div className="scheduled-stream__row__controls">
								<>
									<button onClick={createScheduledStream}>
										<Icon name={ICONS.CHECK} color={COLORS.GREEN} size={16} />
									</button>
									<button onClick={() => setIsCreating(false)}>
										<Icon name={ICONS.CLOSE} color={COLORS.WHITE} size={16} />
									</button>
								</>
							</div>
						</div>
					)}
				</>
			)}
		</div>
	);
}
